import axios from 'axios';
import { ExportCount, ExportFormat, MarkingFilters, MarkingListResponse, MarkingMeta, ProductOption, SortState } from './types';

/** Сервис документов: с портала запросы идут через основной бэкенд (/api/v2/documents → :3003) */
const BASE = '/api/v2/documents/marking';

/** Фильтры → параметры запроса (пустые значения отбрасываются); справочные значения передаются идентификаторами */
export function filtersToParams(f: MarkingFilters): Record<string, string> {
  const p: Record<string, string> = {};
  const put = (key: string, value: string) => { if (value) p[key] = value; };
  put('search', f.search.trim());
  put('type', f.types.join(','));
  put('status', f.statuses.join(','));
  put('pharmacy', f.pharmacies.join(','));
  put('storage', f.storages.join(','));
  put('dateFrom', f.dateFrom);
  put('dateTo', f.dateTo);
  put('productId', f.products.map(x => x.id).join(','));
  return p;
}

export interface DownloadedFile { blob: Blob; filename: string }

/** Расширение по типу содержимого ответа: Content-Type доступен браузеру всегда, в отличие от Content-Disposition */
function extensionFrom(contentType: string, format: ExportFormat): string {
  if (/zip/i.test(contentType)) return 'zip';
  if (/spreadsheetml/i.test(contentType)) return 'xlsx';
  if (/csv/i.test(contentType)) return 'csv';
  return format;
}

/**
 * Имя файла: из Content-Disposition (сервер отдаёт marking_codes_YYYY-MM-DD.xlsx | .csv | .zip).
 * При запросах между разными адресами (dev: localhost -> API) браузер этот заголовок скрывает, тогда имя собирается
 * по типу содержимого - иначе архив сохранялся бы с расширением выбранного формата, а не .zip.
 */
function fileNameFrom(disposition: unknown, contentType: string, format: ExportFormat): string {
  const match = /filename="?([^";]+)"?/i.exec(String(disposition || ''));
  if (match) return match[1];
  return `marking_codes_${new Date().toISOString().slice(0, 10)}.${extensionFrom(contentType, format)}`;
}

export const markingApi = {
  async meta(signal?: AbortSignal): Promise<MarkingMeta & { success: boolean }> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async list(filters: MarkingFilters, sort: SortState, page: number, limit: number, signal?: AbortSignal): Promise<MarkingListResponse> {
    const params = { ...filtersToParams(filters), sortBy: sort.sortBy, sortOrder: sort.sortOrder, page: String(page), limit: String(limit) };
    const res = await axios.get(`${BASE}/`, { params, signal });
    return res.data;
  },

  /** Подбор товаров для фильтра (только позиции из документов в зоне видимости оператора) */
  async materials(q: string, signal?: AbortSignal): Promise<ProductOption[]> {
    const res = await axios.get(`${BASE}/materials`, { params: { q }, signal });
    return res.data.products || [];
  },

  /** Сколько строк и файлов получится при выгрузке по фильтрам */
  async exportCount(filters: MarkingFilters, signal?: AbortSignal): Promise<ExportCount> {
    const res = await axios.get(`${BASE}/export-count`, { params: filtersToParams(filters), signal });
    return res.data;
  },

  /** Один файл либо ZIP со всеми частями - решает сервер (по 100 000 строк в файле) */
  async exportFile(filters: MarkingFilters, format: ExportFormat, columns: string[], signal?: AbortSignal): Promise<DownloadedFile> {
    const res = await axios.get(`${BASE}/export`, {
      params: { ...filtersToParams(filters), format, columns: columns.join(',') },
      responseType: 'blob',
      // файл формируется минутами: общий таймаут axios здесь не нужен, обрыв - через signal
      timeout: 0,
      signal
    });
    const blob: Blob = res.data;
    return { blob, filename: fileNameFrom(res.headers['content-disposition'], blob.type || String(res.headers['content-type'] || ''), format) };
  }
};

/** Источник подсказок для ProductFilter: стабильная ссылка, иначе фильтр перезапрашивает список на каждый рендер */
export const searchMaterials = (q: string, signal: AbortSignal): Promise<ProductOption[]> => markingApi.materials(q, signal);

export const isCancelled = (err: unknown): boolean => axios.isCancel(err);
